import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import api from "./Api";

const AnalyticsDashboard = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [published, drafts] = await Promise.all([
          api.get("/blogs/myarticles"),
          api.get("/blogs/drafts").catch(() => []),
        ]);
        setArticles([...(published || []), ...(drafts || [])]);
      } catch (err) {
        console.error("Analytics fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalViews = articles.reduce((s, a) => s + (a.views || 0), 0);
  const totalLikes = articles.reduce((s, a) => s + (a.likes || 0), 0);

  const chartData = articles
    .sort((a, b) => (b.views || 0) - (a.views || 0))
    .slice(0, 12)
    .map(a => ({
      name: a.title ? (a.title.length > 22 ? a.title.substring(0, 22) + "…" : a.title) : "Untitled",
      Views: a.views || 0,
      Likes: a.likes || 0,
    }));

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Analytics</h2>
        <p style={{ color: "var(--muted-color)" }}>Loading analytics…</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 24, color: "var(--text-color)" }}>Analytics Dashboard</h2>

      {/* Summary Cards */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 32 }}>
        {[
          { label: "Total Articles", value: articles.length, color: "#4a6cf7" },
          { label: "Total Views", value: totalViews.toLocaleString(), color: "#22c55e" },
          { label: "Total Likes", value: totalLikes.toLocaleString(), color: "#f59e0b" },
        ].map(card => (
          <div
            key={card.label}
            style={{
              flex: "1 1 160px", minWidth: 140, padding: "20px 24px",
              background: "var(--card-bg, #fff)", borderRadius: 12,
              boxShadow: "0 2px 10px var(--shadow-color, rgba(0,0,0,0.07))",
              borderTop: `4px solid ${card.color}`,
            }}
          >
            <p style={{ margin: 0, fontSize: 13, color: "var(--muted-color)", textTransform: "uppercase", fontWeight: 600 }}>
              {card.label}
            </p>
            <p style={{ margin: "8px 0 0", fontSize: 28, fontWeight: 700, color: card.color }}>
              {card.value}
            </p>
          </div>
        ))}
      </div>

      {/* Bar Chart */}
      {chartData.length > 0 ? (
        <>
          <h3 style={{ marginBottom: 16, color: "var(--text-color)", fontSize: 16 }}>
            Views &amp; Likes (Top {chartData.length} articles)
          </h3>
          <div style={{ width: "100%", overflowX: "auto" }}>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #eee)" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "var(--muted-color, #666)" }}
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis tick={{ fontSize: 11, fill: "var(--muted-color, #666)" }} />
                <Tooltip
                  contentStyle={{
                    background: "var(--card-bg, #fff)",
                    border: "1px solid var(--border-color, #ddd)",
                    borderRadius: 8,
                    color: "var(--text-color, #222)",
                  }}
                />
                <Legend />
                <Bar dataKey="Views" fill="#4a6cf7" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Likes" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : (
        <p style={{ color: "var(--muted-color)" }}>No article data available yet.</p>
      )}

      {/* Article Table */}
      {articles.length > 0 && (
        <div style={{ marginTop: 32, overflowX: "auto" }}>
          <h3 style={{ marginBottom: 12, color: "var(--text-color)", fontSize: 16 }}>All Articles</h3>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--card-bg)", color: "var(--muted-color)", textAlign: "left" }}>
                {["Title", "Status", "Views", "Likes", "Date"].map(h => (
                  <th key={h} style={{ padding: "10px 12px", borderBottom: "2px solid var(--border-color, #ddd)", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {articles.map((a, i) => (
                <tr key={a.id} style={{ background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.02)" }}>
                  <td style={{ padding: "9px 12px", color: "var(--text-color)", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {a.title || "Untitled"}
                  </td>
                  <td style={{ padding: "9px 12px" }}>
                    <span style={{
                      padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                      background: a.status === "PUBLISHED" ? "#d1fae5" : "#fef3c7",
                      color: a.status === "PUBLISHED" ? "#065f46" : "#92400e",
                    }}>{a.status}</span>
                  </td>
                  <td style={{ padding: "9px 12px", color: "#4a6cf7", fontWeight: 600 }}>{a.views || 0}</td>
                  <td style={{ padding: "9px 12px", color: "#f59e0b", fontWeight: 600 }}>{a.likes || 0}</td>
                  <td style={{ padding: "9px 12px", color: "var(--muted-color)", whiteSpace: "nowrap" }}>
                    {a.createdAt ? new Date(a.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
